import type { ErrorComponentProps } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { Link, useRouter } from "@tanstack/react-router";
import { RotateCw, TriangleAlert, WifiOff } from "lucide-react";

import { Button } from "@gately/ui/button";

import { isServerUnreachable } from "~/lib/is-server-unreachable";
import { isCancelledRouteError } from "~/lib/route-error";
import { StatusScreen } from "./status-screen";

/**
 * Global error boundary UI (`defaultErrorComponent`). Replaces TanStack
 * Router's raw default. Shows friendlier "service unavailable" copy when the
 * error looks like the server being unreachable, a generic message otherwise —
 * never the raw error text. The error is still reported to Sentry so it isn't
 * lost. "Réessayer" re-runs the failed loaders (right after a recovered server)
 * rather than a hard reload.
 *
 * A `CancelledError` (see `isCancelledRouteError`) is not shown at all: the
 * loader's prefetch was aborted by an unmounting observer, so the screen
 * re-runs the loaders once, automatically, and renders nothing meanwhile. One
 * attempt only — if the retry fails for a real reason, that error is a
 * different object and renders normally.
 */
export function ErrorScreen({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const cancelled = isCancelledRouteError(error);
  const autoRetried = useRef(false);

  useEffect(() => {
    if (cancelled) {
      if (autoRetried.current) return;
      autoRetried.current = true;
      reset();
      void router.invalidate();
      return;
    }
    Sentry.captureException(error);
  }, [cancelled, error, reset, router]);

  if (cancelled) return null;

  const unreachable = isServerUnreachable(error);

  const retry = () => {
    reset();
    void router.invalidate();
  };

  return (
    <StatusScreen
      icon={
        unreachable ? (
          <WifiOff className="size-6" />
        ) : (
          <TriangleAlert className="size-6" />
        )
      }
      title={
        unreachable
          ? "Service momentanément indisponible"
          : "Une erreur est survenue"
      }
      description={
        unreachable
          ? "Nous n'arrivons pas à joindre nos serveurs pour le moment. Veuillez réessayer dans quelques instants."
          : "Quelque chose s'est mal passé de notre côté. Réessayez, ou revenez à l'accueil."
      }
      actions={
        <>
          <Button onClick={retry}>
            <RotateCw />
            Réessayer
          </Button>
          <Button asChild variant="outline">
            <Link to="/">Accueil</Link>
          </Button>
        </>
      }
    />
  );
}
